'use server';
/**
 * Story ideas — the plot-idea chips of the wizard. Admin CRUD plus the
 * wizard's read (any signed-in user).
 */
import { and, asc, eq, isNull, or } from 'drizzle-orm';
import { revalidatePath } from 'next/cache';
import { getDrizzleDb } from '@/shared/infrastructure/database/drizzle';
import { actionErrorLog } from '@/shared/infrastructure/logging/actionError';
import { SessionService } from '@/contexts/authentication';
import type { ActionResult } from '@/shared/types/actions';
import { storyIdeas } from '../repositories/schema';
import { LANGS } from '../types';

const PATH = '/admin/story-ideas';
const KEY_RE = /^[a-z0-9][a-z0-9_-]{1,40}$/;

export interface StoryIdea {
  id: string;
  language: string;
  text: string;
  storyTemplateKey: string | null;
  isActive: boolean;
  sortOrder: number;
}

export type StoryIdeaInput = Omit<StoryIdea, 'id'>;

function sanitize(raw: Partial<StoryIdeaInput>): { input?: StoryIdeaInput; error?: string } {
  const language = String(raw.language ?? '').trim();
  const text = String(raw.text ?? '').trim();
  const key = raw.storyTemplateKey ? String(raw.storyTemplateKey).trim() : '';
  if (!(LANGS as readonly string[]).includes(language)) return { error: 'invalid_language' };
  if (text.length < 5 || text.length > 200) return { error: 'text_length' };
  if (key && !KEY_RE.test(key)) return { error: 'invalid_template_key' };
  return {
    input: {
      language,
      text,
      storyTemplateKey: key || null,
      isActive: raw.isActive ?? true,
      sortOrder: Number.isFinite(raw.sortOrder) ? Number(raw.sortOrder) : 0,
    },
  };
}

export async function listStoryIdeas(): Promise<StoryIdea[]> {
  await SessionService.requireRole('admin');
  return getDrizzleDb().select({
    id: storyIdeas.id, language: storyIdeas.language, text: storyIdeas.text,
    storyTemplateKey: storyIdeas.storyTemplateKey, isActive: storyIdeas.isActive, sortOrder: storyIdeas.sortOrder,
  }).from(storyIdeas).orderBy(asc(storyIdeas.language), asc(storyIdeas.sortOrder));
}

export async function saveStoryIdea(id: string | null, raw: Partial<StoryIdeaInput>): Promise<ActionResult<{ id: string }>> {
  await SessionService.requireRole('admin');
  const { input, error } = sanitize(raw);
  if (!input) return { success: false, error: error! };
  try {
    const db = getDrizzleDb();
    if (id) {
      await db.update(storyIdeas).set({ ...input, updatedAt: new Date() }).where(eq(storyIdeas.id, id));
    } else {
      id = `idea_${crypto.randomUUID()}`;
      await db.insert(storyIdeas).values({ id, ...input });
    }
    revalidatePath(PATH);
    return { success: true, data: { id } };
  } catch (e) {
    actionErrorLog('saveStoryIdea', e);
    return { success: false, error: e instanceof Error ? e.message : 'Failed' };
  }
}

export async function deleteStoryIdea(id: string): Promise<ActionResult<{ ok: true }>> {
  await SessionService.requireRole('admin');
  try {
    await getDrizzleDb().delete(storyIdeas).where(eq(storyIdeas.id, id));
    revalidatePath(PATH);
    return { success: true, data: { ok: true } };
  } catch (e) {
    actionErrorLog('deleteStoryIdea', e);
    return { success: false, error: e instanceof Error ? e.message : 'Failed' };
  }
}

/**
 * The wizard's chips: active ideas in `language` for the chosen genre and the
 * ones that suit any genre. null when the table is unavailable (→ built-ins).
 */
export async function wizardIdeas(language: string, templateKey: string | null | undefined): Promise<string[] | null> {
  // The wizard is signed-in only.
  await SessionService.requireSession();
  try {
    const rows = await getDrizzleDb().select({ text: storyIdeas.text }).from(storyIdeas)
      .where(and(
        eq(storyIdeas.language, language),
        eq(storyIdeas.isActive, true),
        templateKey ? or(isNull(storyIdeas.storyTemplateKey), eq(storyIdeas.storyTemplateKey, templateKey)) : isNull(storyIdeas.storyTemplateKey),
      ))
      .orderBy(asc(storyIdeas.sortOrder));
    return rows.map((r) => r.text);
  } catch {
    return null;
  }
}
